import { Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { SelectField } from '@/shared/ui/select';

import { playTestTone } from '../voice/pcm.service';

import type { VoiceDiagnostics, VoiceStatus } from '../voice/voice-session.service';

/** Device choice stays in this browser session; labels and audio never enter learner memory. */
export function MioAudioControls({
  diagnostics,
  microphoneId,
  status,
  muted,
  onSelectMicrophone,
}: {
  diagnostics: VoiceDiagnostics | null;
  microphoneId: string;
  status: VoiceStatus;
  muted: boolean;
  onSelectMicrophone: (deviceId: string) => Promise<void>;
}) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [notice, setNotice] = useState('');
  const [silent, setSilent] = useState(false);
  const lastSound = useRef(Date.now());
  const microphone = diagnostics?.microphone ?? '';
  const rms = diagnostics?.rms ?? 0;
  const level = muted ? 0 : Math.min(100, Math.round(rms * 1200));

  useEffect(() => {
    let alive = true;
    const media = navigator.mediaDevices;
    if (!media?.enumerateDevices) return;
    const refresh = () => {
      void media.enumerateDevices().then(
        (next) => {
          if (alive) setDevices(next.filter((device) => device.kind === 'audioinput'));
        },
        () => {
          if (alive) setNotice('Check browser microphone permission to choose an input.');
        },
      );
    };
    refresh();
    media.addEventListener?.('devicechange', refresh);
    return () => {
      alive = false;
      media.removeEventListener?.('devicechange', refresh);
    };
  }, [microphone, status]);

  useEffect(() => {
    if (rms > 0.0001) lastSound.current = Date.now();
  }, [rms]);
  useEffect(() => {
    lastSound.current = Date.now();
    const timer = setInterval(() => {
      setSilent(!muted && status === 'listening' && Date.now() - lastSound.current > 10_000);
    }, 1000);
    return () => clearInterval(timer);
  }, [microphoneId, muted, status]);

  return (
    <div className="border-line/70 mt-3 space-y-2 border-t pt-2.5">
      <div className="flex items-center gap-2">
        <label className="text-fg-subtle shrink-0 text-xs" htmlFor="mio-microphone">
          Input
        </label>
        <SelectField
          id="mio-microphone"
          aria-label="Microphone"
          value={microphoneId}
          disabled={status === 'connecting'}
          onValueChange={(value) => {
            setNotice('');
            void onSelectMicrophone(value).catch(() => {
              setNotice('Could not switch microphones. Reconnect voice and try again.');
            });
          }}
          size="sm"
          className="min-w-0 flex-1"
          options={[
            { value: '', label: `System default${microphone ? ` · ${microphone}` : ''}` },
            ...(microphoneId && !devices.some((device) => device.deviceId === microphoneId)
              ? [{ value: microphoneId, label: 'Selected microphone unavailable' }]
              : []),
            ...devices
              .filter((device) => device.deviceId !== 'default')
              .map((device, index) => ({
                value: device.deviceId,
                label: device.label || `Microphone ${index + 1}`,
              })),
          ]}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div
            role="meter"
            aria-label="Microphone input level"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={level}
            className="bg-line h-1 w-12 overflow-hidden rounded-full"
          >
            <div
              className="bg-mio h-full transition-[width] duration-100"
              style={{ width: `${level}%` }}
            />
          </div>
          <span className="text-fg-subtle text-xs">
            {muted ? 'Muted' : level > 0 ? 'Input detected' : 'Speak to test'}
          </span>
        </div>
        <button
          type="button"
          className="text-fg-subtle hover:text-fg inline-flex min-h-11 shrink-0 items-center gap-1 text-xs lg:min-h-8"
          onClick={() => {
            setNotice('');
            void playTestTone().then(
              () => setNotice('You should hear a short tone through your selected system output.'),
              () =>
                setNotice(
                  'Audio is blocked. Check browser sound permission and your system output.',
                ),
            );
          }}
        >
          <Volume2 size={14} aria-hidden="true" /> Test speakers
        </button>
      </div>
      {silent && !muted && status === 'listening' ? (
        <p className="text-fg-muted text-xs leading-5" role="status">
          No sound detected. Try speaking, or choose another microphone above.
        </p>
      ) : null}
      {notice ? (
        <p className="text-fg-muted text-xs leading-5" role="status">
          {notice}
        </p>
      ) : null}
    </div>
  );
}
